if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.viewer) return;

      // Dentis CRO enhancements (CTA, nudge, progress) - safe no-op if markup missing
      this.initDentisCro();

      // Default Dawn behavior: only wire thumbnail sync when thumbnails exist
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      const isInitialLoad = !this._hasSetActiveMediaOnce;
      this._hasSetActiveMediaOnce = true;

      this.preventStickyHeader();
      const isCroGallery = this.dataset.dentisCro === 'true';
      window.setTimeout(() => {
        if (isCroGallery) {
          const viewerScroller = this.elements.viewer?.slider || activeMedia.parentElement;
          viewerScroller.scrollTo({ left: activeMedia.offsetLeft, behavior: isInitialLoad ? 'instant' : 'smooth' });
        } else if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        // Avoid page-level jumps on mobile: only use element scrollIntoView fallback on desktop.
        if (!isInitialLoad && !isCroGallery && this.mql.matches && (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked')) {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      const shouldCenterActiveThumb = this.dataset.dentisCro === 'true';
      this.scrollActiveThumbnail(thumbnail, {
        force: shouldCenterActiveThumb,
        behavior: shouldCenterActiveThumb ? 'smooth' : 'auto'
      });
    }

    scrollActiveThumbnail(thumbnail, { force = false, behavior = 'auto' } = {}) {
      const thumbnailSlider = this.elements.thumbnails;
      const scroller = thumbnailSlider?.slider;
      if (!thumbnailSlider || !scroller || !thumbnail) return;
      if (!force && thumbnailSlider.isSlideVisible(thumbnail, 10)) return;

      const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      if (maxScrollLeft <= 0) return;

      const centeredLeft = thumbnail.offsetLeft - (scroller.clientWidth - thumbnail.clientWidth) / 2;
      const targetLeft = Math.max(0, Math.min(centeredLeft, maxScrollLeft));
      scroller.scrollTo({ left: targetLeft, behavior });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    /* ----------------------------------------------------------------------
       Dentis CRO: product gallery engagement helpers
       ---------------------------------------------------------------------- */

    initDentisCro() {
      // Opt-in via data attribute to avoid affecting other media-gallery instances.
      if (this.dataset.dentisCro !== 'true') return;

      const uiRoot = this.elements.viewer?.querySelector('[data-dentis-cro-gallery-ui]');
      if (!uiRoot) return;

      this.dentisCro = {
        ui: uiRoot,
        cta: this.elements.viewer.querySelector('[data-dentis-cro-gallery-cta]'),
        progressFill: this.elements.viewer.querySelector('[data-dentis-cro-gallery-progress-fill]'),
        progressCurrent: this.elements.viewer.querySelector('[data-dentis-cro-gallery-progress-current]'),
        progressTotal: this.elements.viewer.querySelector('[data-dentis-cro-gallery-progress-total]'),
        prevButton: this.elements.viewer.querySelector('button[name="previous"]'),
        nextButton: this.elements.viewer.querySelector('button[name="next"]'),
        sliderEl: this.elements.viewer.slider || this.elements.viewer.querySelector('[id^="Slider-"]'),
        timers: []
      };

      this.dentisCroHasInteracted = false;
      this.dentisCroBounceDelayMs = parseInt(this.dataset.dentisCroBounceDelayMs, 10);
      if (!Number.isFinite(this.dentisCroBounceDelayMs)) this.dentisCroBounceDelayMs = 2000;

      this.dentisCroPrefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const markEngaged = () => {
        if (!this.dentisCroHasInteracted) this.dentisCroHasInteracted = true;
        this.clearDentisCroTimers();
        this.classList.remove('dentis-cro-media-gallery--cta-pulse');
        this.updateDentisCroUI();
      };

      // Engagement signals (do not treat just touching the gallery as engagement)
      this.dentisCro.prevButton?.addEventListener('click', markEngaged);
      this.dentisCro.nextButton?.addEventListener('click', markEngaged);
      this.elements.thumbnails?.addEventListener('click', markEngaged);

      // CTA: explicit “Se mer” prompt on first slide
      if (this.dentisCro.cta) {
        this.dentisCro.cta.addEventListener('click', () => {
          markEngaged();
          this.dentisCro.nextButton?.click();
        });
      }

      // Keep progress + CTA state in sync with the slider
      this.elements.viewer.addEventListener('slideChanged', (event) => {
        const currentPage = event?.detail?.currentPage || this.getDentisCroCurrentPage();
        const currentElement = event?.detail?.currentElement;
        this.updateDentisCroUI(currentPage, currentElement);
      });

      // Recompute on size changes (image load, orientation change, etc.)
      if (window.ResizeObserver && this.dentisCro.sliderEl) {
        this.dentisCro.resizeObserver = new ResizeObserver(() => this.updateDentisCroUI());
        this.dentisCro.resizeObserver.observe(this.dentisCro.sliderEl);
      }

      // Initial paint after slider-component has measured pages
      window.setTimeout(() => {
        this.updateDentisCroUI();
        this.scheduleDentisCroNudge();
      }, 0);
    }

    clearDentisCroTimers() {
      if (!this.dentisCro?.timers?.length) return;
      this.dentisCro.timers.forEach((t) => window.clearTimeout(t));
      this.dentisCro.timers = [];
      this.classList.remove('dentis-cro-media-gallery--nudge');
    }

    getDentisCroCurrentPage() {
      const fromProp = this.elements.viewer?.currentPage;
      if (typeof fromProp === 'number' && fromProp > 0) return fromProp;

      const el = this.elements.viewer?.querySelector('[data-dentis-cro-gallery-progress-current]');
      const n = parseInt(el?.textContent || '1', 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    getDentisCroTotalPages() {
      const fromProp = this.elements.viewer?.totalPages;
      if (typeof fromProp === 'number' && fromProp > 0) return fromProp;

      const el = this.elements.viewer?.querySelector('[data-dentis-cro-gallery-progress-total]');
      const n = parseInt(el?.textContent || '1', 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    getDentisCroCountableSlides() {
      // Prefer SliderComponent's measured list; fall back to DOM query.
      let slides = [];
      if (Array.isArray(this.elements.viewer?.sliderItemsToShow) && this.elements.viewer.sliderItemsToShow.length) {
        slides = Array.from(this.elements.viewer.sliderItemsToShow);
      } else if (this.dentisCro?.sliderEl) {
        slides = Array.from(this.dentisCro.sliderEl.querySelectorAll('[id^="Slide-"]'));
      } else if (this.elements.viewer) {
        slides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
      }

      // Exclude non-selected variant media at the end of the list:
      // Keep only the first slide if it is a variant slide; ignore other `.product__media-item--variant`.
      return slides.filter((el) => {
        if (!el?.classList?.contains('product__media-item--variant')) return true;
        return el.parentElement?.firstElementChild === el;
      });
    }

    updateDentisCroUI(forcedCurrentPage, forcedCurrentElement) {
      if (!this.dentisCro?.ui) return;

      const slides = this.getDentisCroCountableSlides();
      const totalCount = slides.length || 1;

      let currentElement = forcedCurrentElement;
      if (!currentElement) {
        const page = forcedCurrentPage || this.getDentisCroCurrentPage();
        const idx = Math.max(0, page - 1);
        if (Array.isArray(this.elements.viewer?.sliderItemsToShow) && this.elements.viewer.sliderItemsToShow.length) {
          currentElement = this.elements.viewer.sliderItemsToShow[idx];
        }
      }

      let currentCount = 1;
      if (currentElement) {
        const i = slides.indexOf(currentElement);
        if (i !== -1) {
          currentCount = i + 1;
        } else {
          const page = forcedCurrentPage || this.getDentisCroCurrentPage();
          currentCount = Math.max(1, Math.min(totalCount, page));
        }
      } else {
        const page = forcedCurrentPage || this.getDentisCroCurrentPage();
        currentCount = Math.max(1, Math.min(totalCount, page));
      }

      // If they've moved past the first slide, they are engaged.
      if (currentCount > 1 && !this.dentisCroHasInteracted) {
        this.dentisCroHasInteracted = true;
        this.clearDentisCroTimers();
      }

      // Progress bar fill
      if (this.dentisCro.progressFill) {
        const pct = totalCount > 0 ? Math.max(0, Math.min(100, (currentCount / totalCount) * 100)) : 0;
        this.dentisCro.progressFill.style.width = `${pct}%`;
      }

      // Progress numbers (do not use Dawn slider-counter classes; they're updated on scroll)
      if (this.dentisCro.progressCurrent) this.dentisCro.progressCurrent.textContent = String(currentCount);
      if (this.dentisCro.progressTotal) this.dentisCro.progressTotal.textContent = String(totalCount);

      // CTA: only on first slide, only until the first real engagement
      const shouldShowCta = !this.dentisCroHasInteracted && currentCount === 1 && totalCount > 1;
      if (this.dentisCro.cta) this.dentisCro.cta.hidden = !shouldShowCta;

      this.classList.toggle('dentis-cro-media-gallery--cta-visible', shouldShowCta);

      // If CTA is not visible, ensure pulse is off
      if (!shouldShowCta) this.classList.remove('dentis-cro-media-gallery--cta-pulse');

      // Arrow visibility at edges (robust against SliderComponent's scrollLeft === 0 check)
      const atStart = currentCount <= 1;
      const atEnd = currentCount >= totalCount;
      this.classList.toggle('dentis-cro-media-gallery--at-start', atStart);
      this.classList.toggle('dentis-cro-media-gallery--at-end', atEnd);

      // Keep buttons disabled for accessibility (even if SliderComponent toggles them)
      if (this.dentisCro.prevButton) this.dentisCro.prevButton.toggleAttribute('disabled', atStart);
      if (this.dentisCro.nextButton) this.dentisCro.nextButton.toggleAttribute('disabled', atEnd);
    }

    scheduleDentisCroNudge() {
      if (!this.dentisCro?.ui) return;
      if (this.dentisCroPrefersReducedMotion) return;

      const totalCount = this.getDentisCroCountableSlides().length;
      if (totalCount < 2) return;

      const isInView = () => {
        const rect = this.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
      };

      const nudgeOnce = () => {
        if (this.dentisCroHasInteracted) return;
        if (this.getDentisCroCurrentPage() !== 1) return;
        if (!isInView()) return;

        this.classList.add('dentis-cro-media-gallery--nudge');
        const tOff = window.setTimeout(() => this.classList.remove('dentis-cro-media-gallery--nudge'), 950);
        this.dentisCro.timers.push(tOff);
      };

      const repeatEveryMs = Math.max(3500, this.dentisCroBounceDelayMs * 2);
      const loop = () => {
        if (this.dentisCroHasInteracted) return;
        nudgeOnce();
        const tNext = window.setTimeout(loop, repeatEveryMs);
        this.dentisCro.timers.push(tNext);
      };

      const tStart = window.setTimeout(loop, this.dentisCroBounceDelayMs);
      this.dentisCro.timers.push(tStart);

      // Pulse CTA after a moment if still idle
      const tPulse = window.setTimeout(() => {
        if (this.dentisCroHasInteracted) return;
        if (this.getDentisCroCurrentPage() !== 1) return;
        this.classList.add('dentis-cro-media-gallery--cta-pulse');
      }, Math.max(1200, this.dentisCroBounceDelayMs + 600));
      this.dentisCro.timers.push(tPulse);
    }
  });
}
